import { getSurplusesReport } from 'lib/api/reports';
import { Surplus } from 'lib/api/reports/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import SurplusDetails from './SurplusDetails';
type SurplusStatus = 'retained' | 'released' | 'all';

const Surpluses: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [startReleaseDate, setStartReleaseDate] = useState(null);
  const [endReleaseDate, setEndReleaseDate] = useState(null);
  const [surplusStatus, setSurplusStatus] = useState<SurplusStatus>('released');
  const [factoringRequestId, setFactoringRequestId] = useState('');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [folio, setFolio] = useState('');
  const [data, setData] = useState<Surplus[]>([]);
  const [openSurplusDetails, setOpenSurplusDetails] = useState(false);
  const [selectedSurplus, setSelectedSurplus] = useState<Surplus | null>(null);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const filters: Filter[] = [
    {
      label: 'Fecha de liberación',
      type: 'date',
      value: startReleaseDate,
      setter: setStartReleaseDate,
      dateKey: 'startReleaseDate',
    },
    {
      label: 'Fecha de liberación',
      type: 'date',
      value: endReleaseDate,
      setter: setEndReleaseDate,
      dateKey: 'endReleaseDate',
    },
    {
      label: 'Nombre Cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut Cliente',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Folio Documento',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'ID Solicitud',
      type: 'text',
      value: factoringRequestId,
      setter: setFactoringRequestId,
    },
    {
      label: 'Estado',
      type: 'select',
      value: surplusStatus,
      setter: setSurplusStatus,
      options: [
        { label: 'Retenido', value: 'retained' },
        { label: 'Liberado', value: 'released' },
        { label: 'Todos', value: 'all' },
      ],
    },
  ];

  const generateQuery = ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete: boolean;
  }) => {
    let query = '';
    if (businessName)
      query += `&businessName=${businessName.replace('&', '%26')}`;
    if (businessIdentifier)
      query += `&businessIdentifier=${businessIdentifier}`;
    if (factoringRequestId)
      query += `&factoringRequestId=${factoringRequestId}`;
    if (folio) query += `&folio=${folio}`;
    if (startReleaseDate) query += `&startReleaseDate=${startReleaseDate}`;
    if (endReleaseDate) query += `&endReleaseDate=${endReleaseDate}`;
    if (surplusStatus) query += `&surplusStatus=${surplusStatus}`;
    if (page) query += `&page=${page}`;
    if (pageSize) query += `&pageSize=${pageSize}`;
    if (exportComplete) query += `&exportComplete=true`;
    return query;
  };

  const fetchData = async ({
    page = 0,
    pageSize = PAGE_SIZE,
    exportComplete = false,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    const query = generateQuery({
      page,
      pageSize,
      exportComplete,
    });
    const response = await getSurplusesReport(dispatch, query);
    if (response && !exportComplete) {
      setData(response.data);
      setPagination(response.pagination);
    }
    return response;
  };

  const triggerSearch = async () => {
    setLoading(true);
    try {
      await fetchData({
        page: 0,
        pageSize: PAGE_SIZE,
      });
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener los datos');
    }
    setLoading(false);
  };

  const setUp = async () => {
    void triggerSearch();
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleClickRow = (rowId: string) => {
    const surplus = data.find((surplus) => surplus.id === rowId);
    if (surplus) {
      setOpenSurplusDetails(true);
      setSelectedSurplus(surplus);
    }
  };

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Acá verás todos los excedentes generados por documentos."
        buttons={[
          {
            name: 'Buscar',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
                shortcuts={true}
              />
            ),
          },
        ]}
      />

      <TableWithPagination
        dataHeaders={[
          { key: 'documentFolio', label: 'Folio documento' },
          { key: 'businessIdentifier', label: 'Rut cliente' },
          { key: 'businessName', label: 'Nombre cliente' },
          { key: 'factoringRequestId', label: 'ID solicitud' },
          { key: 'factoringRequestStatus', label: 'Estado solicitud' },
          { key: 'documentAmount', label: 'Monto documento', type: 'money' },
          {
            key: 'documentAdvance',
            label: 'Porcentaje anticipo',
            type: 'percentage-soft',
          },
          { key: 'documentAdvanced', label: 'Monto anticipado', type: 'money' },
          {
            key: 'documentConciliationAmount',
            label: 'Conciliado documento',
            type: 'money',
          },
          {
            key: 'arrearConciliationAmount',
            label: 'Conciliado mora',
            type: 'money',
          },
          {
            key: 'totalConciliationAmount',
            label: 'Conciliado total',
            type: 'money',
          },
          {
            key: 'factoringRequestOperationDate',
            label: 'Fecha operación solicitud',
            type: 'date',
          },
          {
            key: 'documentPaymentDate',
            label: 'Fecha vencimiento documento',
            type: 'date',
          },
          {
            key: 'documentPaidAt',
            label: 'Fecha pago documento',
            type: 'date',
          },
          {
            key: 'retainedPercentage',
            label: 'Porcentaje retenido',
            type: 'percentage-soft',
          },
          { key: 'retainedAmount', label: 'Monto retenido', type: 'money' },
          {
            key: 'surplusReducedByArrear',
            label: 'Excedente reducido por mora',
            type: 'money',
          },
          {
            key: 'surplusReducedByDocument',
            label: 'Excedente reducido por documento',
            type: 'money',
          },
          {
            key: 'availableSurplus',
            label: 'Excedente disponible a liberar',
            type: 'money',
          },
          { key: 'surplusDocumentFolio', label: 'Folio excedente liberado' },
          {
            key: 'surplusDocumentAmount',
            label: 'Monto excedente liberado',
            type: 'money',
          },
          {
            key: 'surplusCreatedAt',
            label: 'Fecha liberación excedente',
            type: 'date',
          },
          {
            key: 'surplusDocumentConciliationAmount',
            label: 'Monto excedente pagado',
            type: 'money',
          },
          {
            key: 'surplusDocumentPaidAt',
            label: 'Fecha pago excedente liberado',
            type: 'date',
          },
        ]}
        data={data}
        rowsPerPageDefault={PAGE_SIZE}
        loading={loading}
        setLoading={setLoading}
        searchData={fetchData}
        exportToFile="xlsx"
        pagination={pagination}
        setPagination={setPagination}
        setData={setData}
        handleClickRow={handleClickRow}
        lastElement
        exportComplete
      />
      {openSurplusDetails && (
        <SurplusDetails
          open={openSurplusDetails}
          setOpen={setOpenSurplusDetails}
          surplus={selectedSurplus}
        />
      )}
    </div>
  );
};

export default Surpluses;
